import { startExternalAuthentication, type ExternalAuthProvider } from "@/api/auth";
import { Button } from "@/components/ui/button";
import { canUseRequiredCookies } from "./required-cookie";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { AuthShell } from "@/components/layout/AuthShell";
import { nativeUpdateAuthenticationReturnTo } from "@/api/local-gateway-update";

const REAUTHENTICATION_TTL_MS = 10 * 60 * 1_000;

export function AuthCompletePage() {
  const { refreshMe } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const started = useRef(false);
  const [failed, setFailed] = useState(false);
  const [provider] = useState<ExternalAuthProvider | null>(() => {
    const value = sessionStorage.getItem("kdrive.auth.provider");
    return value === "google" || value === "microsoft" ? value : null;
  });
  const signupInFlight = useRef(false);
  const [signupBusy, setSignupBusy] = useState(false);
  const [signupError, setSignupError] = useState("");
  const startSignup = async () => {
    if (!provider || signupInFlight.current) return;
    if (!canUseRequiredCookies()) {
      setSignupError("登録を続けるにはCookieを有効にしてください。");
      return;
    }
    signupInFlight.current = true;
    setSignupBusy(true);
    setSignupError("");
    try {
      await startExternalAuthentication(provider, { intent: "signup" });
    } catch {
      signupInFlight.current = false;
      setSignupBusy(false);
      setSignupError("登録を開始できませんでした。時間をおいて再度お試しください。");
    }
  };
  const [signupRequired, setSignupRequired] = useState(false);
  const [sessionEnded, setSessionEnded] = useState(false);
  const [expired, setExpired] = useState(false);
  const [failedIntent, setFailedIntent] = useState<string | null>(null);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const intent = sessionStorage.getItem("kdrive.auth.intent");
    sessionStorage.removeItem("kdrive.auth.intent");
    const result = new URLSearchParams(location.hash.slice(1)).get("result");
    if (result === "signup_required") {
      sessionStorage.removeItem("kdrive.auth.return");
      setSignupRequired(true);
      return;
    }
    sessionStorage.removeItem("kdrive.auth.provider");
    if (result !== "success") {
      sessionStorage.removeItem("kdrive.auth.return");
      setFailedIntent(intent);
      setExpired(result === "expired");
      setFailed(true);
      return;
    }
    if (intent === "signup") {
      sessionStorage.removeItem("kdrive.auth.return");
      navigate("/signup/external", { replace: true });
      return;
    }
    void refreshMe().then(
      () => {
        if (intent === "reauthenticate") {
          sessionStorage.setItem("kdrive.auth.accountExternalProof", "true");
          sessionStorage.setItem(
            "kdrive.auth.reauthenticatedExpiresAt",
            new Date(Date.now() + REAUTHENTICATION_TTL_MS).toISOString(),
          );
        }
        const returnTo = sessionStorage.getItem("kdrive.auth.return");
        sessionStorage.removeItem("kdrive.auth.return");
        navigate(returnTo === "/console/account/security" ? returnTo
          : nativeUpdateAuthenticationReturnTo(returnTo) ?? "/console/dashboard", { replace: true });
      },
      () => {
        sessionStorage.removeItem("kdrive.auth.return");
        if (intent === "link") setSessionEnded(true);
        else setFailed(true);
      },
    );
  }, [location.hash, navigate, refreshMe]);

  if (signupRequired) return (
    <AuthShell title="新規登録が必要です" description="外部認証は完了しましたが、このGoogle／Microsoftアカウントはログイン方法として登録されていません。">
      <div className="space-y-4">
        {signupError ? <p role="alert" className="text-sm text-destructive">{signupError}</p> : null}
        {provider ? <Button className="w-full" disabled={signupBusy} onClick={() => void startSignup()}>
          {provider === "google" ? "Google" : "Microsoft"}で新規登録する
        </Button> : null}
        <Link className="block text-primary underline" to="/signup">{provider ? "別の認証方法で登録する" : "新規登録へ進む"}</Link>
        <p className="text-sm text-muted-foreground">すでにEXTONE Driveのアカウントをお持ちの場合は、登録済みの方法でログインし、アカウントのセキュリティ設定からこの外部アカウントを追加してください。</p>
        <Link className="text-primary underline" to="/login">別の方法でログインする</Link>
      </div>
    </AuthShell>
  );

  return (
    <AuthShell
      title={sessionEnded ? "再ログインしてください" : expired ? "認証の待ち時間を超えました" : failed ? "認証を完了できませんでした" : "認証を確認しています"}
      description={sessionEnded
        ? "ログイン方法の処理後は安全のためセッションを終了します。再ログインして現在の状態を確認してください。"
        : expired ? "認証を開始してから30分が経過しました。最初からやり直し、アカウントを選択してください。"
        : failed ? "認証情報やアカウントの有無を区別しない共通結果です。" : "安全な画面へ移動します。"}
    >
      {failed || sessionEnded
        ? <Link className="text-primary underline" to={failedIntent === "signup" ? "/signup" : "/login"}>
            {failedIntent === "signup" ? "登録からやり直す" : "ログインからやり直す"}
          </Link>
        : <p role="status">確認中…</p>}
    </AuthShell>
  );
}
